import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Layout from "./components/Layout";
import CommandCentre from "./pages/CommandCentre";
import CommandCentreOld from "./pages/CommandCentreOld";
import TrusteeOverview from "./pages/TrusteeOverview";
import Grants from "./pages/Grants";
import ExecutiveOverview from "./pages/ExecutiveOverview";
import Financial from "./pages/Financial";
import ThematicOverview from "./pages/ThematicOverview";
import GeographicFootprint from "./pages/GeographicFootprint";
import GranteeOverview from "./pages/GranteeOverview";
import DataHealth from "./pages/DataHealth";
import RawData from "./pages/RawData";
import Logics from "./pages/Logics";
import GrantsOverview from "./pages/GrantsOverview";
import RealTrustee from "./pages/RealTrustee";
import RealExecutive from "./pages/RealExecutive";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import { FilterProvider } from "./context/FilterContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import axios from "axios";
import "./App.css";

// Send cookies with every request for authentication
axios.defaults.withCredentials = true;

// Raw-data mode interceptor: when the user is on a /real-* page, every outbound
// API call automatically has `raw=true` appended to its query string. The
// backend uses this flag to route reads through the pre-override `raw` schema
// snapshot. One-liner that keeps the Real-Trustee / Real-Executive pages in
// perfect visual sync with the originals (same hook, same components) while
// transparently switching the data source.
axios.interceptors.request.use((config) => {
  try {
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/real-")) {
      config.params = { ...(config.params || {}), raw: "true" };
    }
  } catch { /* no-op */ }
  return config;
});

function FullScreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center px-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#B71C1C] mx-auto mb-3" />
        <p className="text-sm text-slate-700 font-medium">Loading dashboard…</p>
      </div>
    </div>
  );
}

function PrivateApp() {
  return (
    <FilterProvider>
      <Layout>
        <Routes>
          <Route path="/" element={<Navigate to="/trustee" replace />} />
          <Route path="/trustee" element={<TrusteeOverview />} />
          <Route path="/grants" element={<Grants />} />
          <Route path="/executive" element={<CommandCentre />} />
          <Route path="/executive-old" element={<CommandCentreOld />} />
          <Route path="/executive-legacy" element={<ExecutiveOverview />} />
          <Route path="/financial" element={<Financial />} />
          <Route path="/thematic" element={<ThematicOverview />} />
          <Route path="/geographic" element={<GeographicFootprint />} />
          <Route path="/grantee" element={<GranteeOverview />} />
          <Route path="/impact" element={<Navigate to="/trustee" replace />} />
          <Route path="/data-health" element={<DataHealth />} />
          <Route path="/raw-data" element={<RawData />} />
          <Route path="/logics" element={<Logics />} />
          <Route path="/grant-overview" element={<GrantsOverview />} />
          <Route path="/real-trustee" element={<RealTrustee />} />
          <Route path="/real-executive" element={<RealExecutive />} />
          <Route path="*" element={<Navigate to="/trustee" replace />} />
        </Routes>
      </Layout>
    </FilterProvider>
  );
}

function RootGate() {
  const { user, loading } = useAuth();
  const location = useLocation();

  // /auth/callback must always render — it's how the session gets established.
  if (location.pathname === "/auth/callback") {
    return <Routes><Route path="/auth/callback" element={<AuthCallback />} /></Routes>;
  }

  if (loading) return <FullScreenLoader />;

  if (!user) {
    // Anything other than /login → bounce to /login.
    if (location.pathname !== "/login") {
      return <Navigate to="/login" replace />;
    }
    return <Routes><Route path="/login" element={<Login />} /></Routes>;
  }

  // Authenticated — block access to /login itself.
  if (location.pathname === "/login") return <Navigate to="/trustee" replace />;
  return <PrivateApp />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <RootGate />
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
